import api from "./api";

/**
 * Social Authentication Service
 * Handles OAuth flows and social media account management
 */

// Platform configurations
export const PLATFORMS = {
  facebook: {
    name: "Facebook",
    color: "bg-facebook",
    textColor: "text-white",
    icon: "👥",
    scopes: [
      "pages_show_list",
      "pages_read_engagement",
      "ads_read",
      "read_insights",
    ],
    capabilities: [
      "Posts",
      "Engagement",
      "Reach",
      "Page Insights",
      "Ad Performance",
    ],
  },
  instagram: {
    name: "Instagram",
    color: "bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500",
    textColor: "text-white",
    icon: "📷",
    scopes: ["instagram_basic", "instagram_manage_insights"],
    capabilities: [
      "Posts",
      "Stories",
      "Reels",
      "Followers",
      "Engagement",
      "Hashtag Performance",
    ],
    businessAccountRequired: true,
    refreshFrequency: {
      standard: "Hourly",
      premium: "Real-time",
    },
    historicalData: "Up to 2 years",
    specialRequirements: [
      "Instagram Business Account required",
      "Facebook Page connection needed",
      "Professional account recommended",
    ],
  },
  threads: {
    name: "Threads",
    color: "bg-black",
    textColor: "text-white",
    icon: "🧵",
    scopes: ["threads_basic", "threads_content_publish"],
    capabilities: [
      "Posts",
      "Engagement",
      "Follower Growth",
      "Thread Performance",
    ],
  },
  twitter: {
    name: "X (Twitter)",
    color: "bg-black",
    textColor: "text-white",
    icon: "🐦",
    scopes: ["tweet.read", "users.read", "follows.read", "like.read"],
    capabilities: [
      "Tweets",
      "Engagement",
      "Impressions",
      "Follower Growth",
      "Tweet Performance",
    ],
  },
  linkedin: {
    name: "LinkedIn",
    color: "bg-linkedin",
    textColor: "text-white",
    icon: "💼",
    scopes: ["r_liteprofile", "r_emailaddress", "w_member_social"],
    capabilities: [
      "Posts",
      "Engagement",
      "Profile Views",
      "Connection Growth",
      "Company Updates",
    ],
  },
  tiktok: {
    name: "TikTok",
    color: "bg-black",
    textColor: "text-white",
    icon: "🎵",
    scopes: ["user.info.basic", "video.list"],
    capabilities: [
      "Videos",
      "Views",
      "Engagement",
      "Follower Growth",
      "Trending Content",
    ],
  },
};

/**
 * Social Accounts API
 */
export const socialAPI = {
  // Get all connected social accounts
  getAccounts: () => api.get("/social-accounts"),

  // Get specific account by ID
  getAccount: (accountId) => api.get(`/social-accounts/${accountId}`),

  // Disconnect an account
  disconnect: (accountId) =>
    api.post(`/social-accounts/${accountId}/disconnect`),

  // Refresh account connection/tokens
  refresh: (accountId) => api.post(`/social-accounts/${accountId}/refresh`),

  // Get account metrics
  getMetrics: (accountId, timeRange = "7d") =>
    api.get(`/social-accounts/${accountId}/metrics?time_range=${timeRange}`),

  // Bulk refresh all accounts
  refreshAll: () => api.post("/social-accounts/refresh-all"),

  // Get account health status
  getHealthStatus: () => api.get("/social-accounts/health"),

  // Get user's subscription limits and usage
  getLimits: () => api.get("/social/limits"),

  // Check if user can add a new connection
  checkConnectionAllowed: () => api.get("/social/limits/check"),

  // Get upgrade options
  getUpgradeOptions: () => api.get("/social/upgrade/options"),
};

/**
 * OAuth API
 */
export const oauthAPI = {
  // Get OAuth authorization URL for a platform
  getAuthUrl: (platform, scopes = null) => {
    const requestBody = { platform };
    if (scopes) {
      requestBody.scopes = Array.isArray(scopes) ? scopes : scopes.split(",");
    } else if (PLATFORMS[platform]?.scopes) {
      requestBody.scopes = PLATFORMS[platform].scopes;
    }

    return api.post("/oauth/authorize-url", requestBody);
  },

  // Handle OAuth callback
  handleCallback: (code, state, platform) =>
    api.post("/oauth/callback", { code, state, platform }),

  // Check OAuth status
  checkStatus: () => api.get("/oauth/check-status"),

  // Revoke OAuth tokens
  revoke: (accountId) => api.post(`/oauth/revoke/${accountId}`),
};

/**
 * OAuth Helper Functions
 */
export class OAuthManager {
  constructor(options = {}) {
    this.popupWindow = null;
    this.pollTimer = null;
    this.messageListener = null;
    this.options = {
      popupWidth: 600,
      popupHeight: 700,
      pollInterval: 1000,
      timeout: 300000, // 5 minutes
      ...options,
    };
  }

  /**
   * Initiate OAuth flow for a platform
   */
  async initiateOAuth(platform, customScopes = null) {
    try {
      // Get authorization URL
      const response = await oauthAPI.getAuthUrl(platform, customScopes);
      const { auth_url, state } = response.data;

      if (!auth_url) {
        throw new Error("No authorization URL received");
      }

      // Open OAuth popup
      return this.openOAuthPopup(auth_url, platform, state);
    } catch (error) {
      throw new Error(`Failed to initiate OAuth: ${error.message}`);
    }
  }

  /**
   * Open OAuth popup window
   */
  openOAuthPopup(authUrl, platform, state) {
    return new Promise((resolve, reject) => {
      // Calculate popup position
      const left = (window.screen.width - this.options.popupWidth) / 2;
      const top = (window.screen.height - this.options.popupHeight) / 2;

      const popupFeatures = [
        `width=${this.options.popupWidth}`,
        `height=${this.options.popupHeight}`,
        `left=${left}`,
        `top=${top}`,
        "scrollbars=yes",
        "resizable=yes",
        "status=no",
        "menubar=no",
        "toolbar=no",
        "location=no",
      ].join(",");

      // Open popup
      this.popupWindow = window.open(
        authUrl,
        `oauth_${platform}_${state}`,
        popupFeatures,
      );

      if (!this.popupWindow) {
        reject(
          new Error("Popup was blocked. Please allow popups for this site."),
        );
        return;
      }

      // Focus popup
      this.popupWindow.focus();

      // Set up timeout
      const timeoutId = setTimeout(() => {
        this.cleanup();
        reject(new Error("OAuth flow timed out"));
      }, this.options.timeout);

      // Listen for messages from popup
      this.messageListener = (event) => {
        // Verify origin
        if (event.origin !== window.location.origin) {
          return;
        }

        if (event.data.type === "oauth_success") {
          clearTimeout(timeoutId);
          this.cleanup();
          resolve({
            success: true,
            account: event.data.account,
            platform: event.data.platform,
          });
        } else if (event.data.type === "oauth_error") {
          clearTimeout(timeoutId);
          this.cleanup();
          reject(new Error(event.data.error || "OAuth authentication failed"));
        }
      };

      window.addEventListener("message", this.messageListener);

      // Poll for popup closure (fallback)
      this.pollTimer = setInterval(() => {
        try {
          if (this.popupWindow.closed) {
            clearTimeout(timeoutId);
            this.cleanup();
            reject(new Error("OAuth flow was cancelled"));
          }
        } catch (error) {
          // Cross-origin error when popup is still on OAuth provider
          // This is normal and expected
        }
      }, this.options.pollInterval);
    });
  }

  /**
   * Clean up OAuth resources
   */
  cleanup() {
    if (this.popupWindow && !this.popupWindow.closed) {
      this.popupWindow.close();
    }
    this.popupWindow = null;

    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }

    if (this.messageListener) {
      window.removeEventListener("message", this.messageListener);
      this.messageListener = null;
    }
  }

  /**
   * Check if OAuth is in progress
   */
  isInProgress() {
    return !!(this.popupWindow && !this.popupWindow.closed);
  }

  /**
   * Cancel current OAuth flow
   */
  cancel() {
    this.cleanup();
  }
}

/**
 * Account Health Utilities
 */
export const healthUtils = {
  /**
   * Get health status for an account
   */
  getAccountHealth(account) {
    const { status, token_status, last_sync, error_count } = account;

    // Check for critical issues
    if (status === "error" || token_status === "expired") {
      return {
        level: "critical",
        status: "error",
        message:
          token_status === "expired" ? "Token expired" : "Connection error",
        color: "text-red-600",
        bg: "bg-red-50",
        border: "border-red-200",
        action: "refresh",
      };
    }

    // Check for warnings
    if (status === "warning" || token_status === "expiring_soon") {
      return {
        level: "warning",
        status: "warning",
        message:
          token_status === "expiring_soon"
            ? "Token expiring soon"
            : "Needs attention",
        color: "text-amber-600",
        bg: "bg-amber-50",
        border: "border-amber-200",
        action: "refresh",
      };
    }

    // Check data freshness
    if (!last_sync) {
      return {
        level: "warning",
        status: "warning",
        message: "No data synced",
        color: "text-amber-600",
        bg: "bg-amber-50",
        border: "border-amber-200",
        action: "sync",
      };
    }

    const lastSyncDate = new Date(last_sync);
    const hoursSinceSync = (new Date() - lastSyncDate) / (1000 * 60 * 60);

    if (error_count > 5 || hoursSinceSync > 48) {
      return {
        level: "warning",
        status: "warning",
        message: "Sync issues detected",
        color: "text-amber-600",
        bg: "bg-amber-50",
        border: "border-amber-200",
        action: "refresh",
      };
    }

    // All good
    return {
      level: "healthy",
      status: "healthy",
      message: "Connected and syncing",
      color: "text-green-600",
      bg: "bg-green-50",
      border: "border-green-200",
      action: null,
    };
  },

  /**
   * Get overall health score for all accounts
   */
  getOverallHealth(accounts) {
    if (!accounts.length) {
      return {
        score: 0,
        level: "no-accounts",
        healthy: 0,
        warning: 0,
        error: 0,
      };
    }

    const healthStats = accounts.reduce(
      (stats, account) => {
        const health = this.getAccountHealth(account);
        switch (health.level) {
          case "healthy":
            stats.healthy++;
            break;
          case "warning":
            stats.warning++;
            break;
          case "critical":
            stats.error++;
            break;
          default:
            // Unknown health level
            break;
        }
        return stats;
      },
      { healthy: 0, warning: 0, error: 0 },
    );

    const score = (healthStats.healthy / accounts.length) * 100;

    let level = "healthy";
    if (healthStats.error > 0) {
      level = "error";
    } else if (healthStats.warning > 0) {
      level = "warning";
    }

    return {
      score: Math.round(score),
      level,
      ...healthStats,
    };
  },

  /**
   * Format last sync time
   */
  formatLastSync(timestamp) {
    if (!timestamp) {
      return { text: "Never", status: "error" };
    }

    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffMinutes < 5) {
      return { text: "Just now", status: "success" };
    }

    if (diffMinutes < 60) {
      return {
        text: `${diffMinutes}m ago`,
        status: diffMinutes < 15 ? "success" : "warning",
      };
    }

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      return {
        text: `${diffHours}h ago`,
        status: diffHours < 6 ? "warning" : "error",
      };
    }

    const diffDays = Math.floor(diffHours / 24);
    return {
      text: `${diffDays}d ago`,
      status: "error",
    };
  },

  /**
   * Get token expiry info
   */
  getTokenExpiry(expiresAt) {
    if (!expiresAt) {
      return null;
    }

    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const diffMs = expiryDate - now;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return {
        status: "expired",
        message: "Token expired",
        daysLeft: 0,
        percentage: 0,
      };
    }

    if (diffDays <= 7) {
      return {
        status: "expiring_soon",
        message: `Expires in ${diffDays} day${diffDays !== 1 ? "s" : ""}`,
        daysLeft: diffDays,
        percentage: Math.max(0, (diffDays / 30) * 100),
      };
    }

    return {
      status: "valid",
      message: `Expires in ${diffDays} days`,
      daysLeft: diffDays,
      percentage: Math.min(100, (diffDays / 30) * 100),
    };
  },
};

/**
 * Subscription tier utilities
 */
export const subscriptionUtils = {
  /**
   * Get tier limits from backend response
   */
  getTierLimits(tier) {
    const limits = {
      free: {
        max_connections: 3,
        data_retention_days: 30,
      },
      standard: {
        max_connections: 10,
        data_retention_days: 90,
      },
      premium: {
        max_connections: 50,
        data_retention_days: 365,
      },
    };
    return limits[tier?.toLowerCase()] || limits.free;
  },

  /**
   * Get account limit for subscription tier
   */
  getAccountLimit(subscription) {
    if (!subscription || subscription.tier === "FREE") {
      return 3;
    }
    if (subscription.tier === "STANDARD") {
      return 10;
    }
    if (subscription.tier === "PREMIUM") {
      return 50;
    }
    return 3; // Default to free
  },

  /**
   * Check if user can connect more accounts
   */
  canConnectMore(connectedCount, subscription) {
    const limit = this.getAccountLimit(subscription);
    return connectedCount < limit;
  },

  /**
   * Get usage status
   */
  getUsageStatus(connectedCount, subscription) {
    const limit = this.getAccountLimit(subscription);

    if (limit >= 50) {
      return {
        text: `${connectedCount} accounts connected`,
        color: "text-green-600",
        unlimited: true,
        percentage: 0,
      };
    }

    const percentage = (connectedCount / limit) * 100;

    if (percentage >= 100) {
      return {
        text: `${connectedCount}/${limit} (At limit)`,
        color: "text-red-600",
        unlimited: false,
        percentage: 100,
      };
    }

    if (percentage >= 80) {
      return {
        text: `${connectedCount}/${limit} (Near limit)`,
        color: "text-amber-600",
        unlimited: false,
        percentage,
      };
    }

    return {
      text: `${connectedCount}/${limit}`,
      color: "text-gray-600",
      unlimited: false,
      percentage,
    };
  },
};

/**
 * Error handling utilities
 */
export const errorUtils = {
  /**
   * Parse OAuth error from URL or response
   */
  parseOAuthError(error, errorDescription) {
    const errorMap = {
      access_denied: "You denied access to your account",
      invalid_request: "Invalid OAuth request",
      invalid_client: "Invalid application credentials",
      invalid_grant: "Invalid authorization code",
      invalid_scope: "Invalid permissions requested",
      server_error: "OAuth provider server error",
      temporarily_unavailable: "OAuth provider temporarily unavailable",
    };

    return (
      errorMap[error] || errorDescription || "An unknown OAuth error occurred"
    );
  },

  /**
   * Get user-friendly error message
   */
  getFriendlyError(error) {
    if (typeof error === "string") {
      return error;
    }

    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;

      switch (status) {
        case 400:
          return data.error || "Invalid request";
        case 401:
          return "Authentication failed. Please try again.";
        case 403:
          return data.message || "Permission denied. Check your account permissions.";
        case 404:
          return "Service not found. Please contact support.";
        case 429:
          return "Too many requests. Please wait and try again.";
        case 500:
          return "Server error. Please try again later.";
        default:
          return data.error || `Request failed with status ${status}`;
      }
    }

    if (error.message) {
      return error.message;
    }

    return "An unexpected error occurred";
  },
};

// Create default OAuth manager instance
export const defaultOAuthManager = new OAuthManager();

// Export convenience methods
export const initiateOAuth = (platform, scopes) =>
  defaultOAuthManager.initiateOAuth(platform, scopes);

export const cancelOAuth = () => defaultOAuthManager.cancel();

export const isOAuthInProgress = () => defaultOAuthManager.isInProgress();
